import { themeConstants } from '@paywithextend/components'
import Button from 'ext-ui/components/Button/Button'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch, useHistory } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  bulkInvitePeopleLocation,
  removeBulkInviteQueryParam,
} from 'src/app/BulkInvite/bulkInvite.routes'
import BulkInviteModal from 'src/app/BulkInvite/BulkInviteModal'
import { ButtonDesigns, ButtonTypes } from 'src/components/Button/Button'
import LayoutShell, {
  LayoutShellButton,
} from 'src/components/LayoutShell/LayoutShell'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import { SubnavDesign, SubnavOption } from 'src/components/Subnav/Subnav'
import { useTranslate } from 'src/localization/Trns'
import PeopleAppRoute from 'src/navigation/appRoutes/people.appRoute'
import { Rdrct } from 'src/navigation/Lnk'
import {
  invitePeopleUrl,
  peopleInvitedToOrgUrl,
  peopleMembersUrl,
} from 'src/navigation/urls'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectPeopleInvitedToOrgMatch,
  selectPeopleMembersMatch,
  selectQueryParams,
} from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

const People: React.FC = () => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const history = useHistory()

  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const peopleMembersMatch = useSelector(selectPeopleMembersMatch)
  const peopleInvitedToOrgMatch = useSelector(selectPeopleInvitedToOrgMatch)
  const currentUser = useSelector(selectUserListModelFromState)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const bulkInviteCsvEnabled = useSelector(
    selectFeatureFlaggerFromState,
  ).bulk_invite_csv
  const queryParams = useSelector(selectQueryParams)
  // The bulk-invite modal layers over whichever People tab is active, deep-linked
  // via a query param so it survives refresh. Hosted here (not in a tab page) so
  // it overlays the origin tab and returns there on close. Gated on the
  // remote-config flag + admin access.
  const showBulkInvite =
    bulkInviteCsvEnabled &&
    Boolean(currentUser?.hasOrgAdminPermissions) &&
    queryParams.bulkInvite === '1'

  const subnavOptions: SubnavOption[] = useMemo(() => {
    const baseOptions: SubnavOption[] =
      currentUser?.isInExplicitOrg &&
      (organization?.memberListVisible ||
        currentUser?.hasOrgCardManagerPermissions ||
        currentUser?.hasOrgAdminPermissions)
        ? [
            {
              label: translate('Organization Members'),
              to: peopleMembersUrl(),
              isActive: peopleMembersMatch !== null,
              ariaLabelSource: 'Organization Members',
            },
          ]
        : []

    return currentUser?.hasOrgAdminPermissions
      ? [
          ...baseOptions,
          {
            label: translate('Invited To Org'),
            to: peopleInvitedToOrgUrl(),
            isActive: peopleInvitedToOrgMatch !== null,
            ariaLabelSource: 'Invited To Org',
          },
        ]
      : baseOptions
  }, [
    peopleMembersMatch,
    peopleInvitedToOrgMatch,
    currentUser,
    organization,
    translate,
  ])

  const inviteAnalyticsEvent = peopleMembersMatch
    ? 'organization members - invite people - text link press'
    : peopleInvitedToOrgMatch
      ? 'invited to org - invite people - text link press'
      : 'invite people - text link press'

  const buttons: LayoutShellButton[] | undefined =
    currentUser?.hasOrgAdminPermissions
      ? [
          ...(bulkInviteCsvEnabled
            ? [
                <Button
                  key="bulkImport"
                  variant="secondary"
                  dropShadow={false}
                  testId="bulkInvitePeople"
                  aria-label={translate('Bulk Import')}
                  onClick={(): void => {
                    analytics?.track('people - bulk import - button press', {
                      intent: 'bulk invite',
                      interaction: 'button press',
                    })
                    history.push(
                      bulkInvitePeopleLocation(calculatedBackgroundLocation),
                    )
                  }}
                >
                  {translate('Bulk Import')}
                </Button>,
              ]
            : []),
          {
            className: 'people-invite-button--no-shadow',
            design: ButtonDesigns.designCta,
            type: ButtonTypes.button,
            text: translate('Invite People'),
            testId: 'invitePeople',
            linkProps: {
              to: invitePeopleUrl(),
              ariaLabel: 'Invite People',
            },
            onClick: (): void => {
              analytics?.track(inviteAnalyticsEvent, {
                intent: 'invite people',
                interaction: 'button press',
              })
            },
          },
        ]
      : undefined

  return (
    <>
      <LayoutShell
        title={translate('People')}
        subnavDesign={SubnavDesign.Floating}
        subnavOptions={currentUser?.isInExplicitOrg ? subnavOptions : []}
        buttons={buttons}
        css={css`
          .people-invite-button--no-shadow {
            box-shadow: inset 0 0 0 2px ${themeConstants.colors.cta_dark};

            &:hover {
              box-shadow: inset 0 0 0 2px ${themeConstants.colors.cta_dark};
            }
          }
        `}
        content={
          <Switch location={calculatedBackgroundLocation}>
            {PeopleAppRoute.subRoutes.map((route, i) => (
              <Route key={i} {...route.routeProps}>
                {route.component}
              </Route>
            ))}
            <Route {...PeopleAppRoute.routeProps} exact>
              <Rdrct to={peopleMembersUrl()} />
            </Route>
            <Route path={'/'}>
              <NotFoundBackgroundPageContent />
            </Route>
          </Switch>
        }
      />
      {showBulkInvite && (
        <BulkInviteModal
          onClose={(): void =>
            history.push(removeBulkInviteQueryParam(history.location))
          }
        />
      )}
    </>
  )
}

export default People
